// src/edu-world/core/placement-editor.ts
// PlacementEditor: dev tool to drag NPCs and micro-interactions to desired positions.
// Press N to toggle editor mode. Drag sprites. Press S to export positions to console (copy to JSON).
// Press Z to zoom out (see full map), press X to zoom back in.

import Phaser from 'phaser';

export class PlacementEditor {
  private scene: Phaser.Scene;
  private active: boolean = false;
  private dragging: Phaser.GameObjects.Image | null = null;
  private dragOffset = { x: 0, y: 0 };
  private labels: Phaser.GameObjects.Text[] = [];
  private editableSprites: { sprite: Phaser.GameObjects.Image; id: string; type: string }[] = [];
  private statusText: Phaser.GameObjects.Text | null = null;
  private playerController: any = null; // reference to lock input

  constructor(scene: Phaser.Scene, playerController?: any) {
    this.scene = scene;
    this.playerController = playerController;

    // Listen for N key to toggle
    scene.input.keyboard?.on('keydown-N', () => this.toggle());
    // Listen for S key to save/export
    scene.input.keyboard?.on('keydown-S', () => { if (this.active) this.exportPositions(); });
    // Z to zoom out, X to zoom in
    scene.input.keyboard?.on('keydown-Z', () => { if (this.active) scene.cameras.main.setZoom(0.8); });
    scene.input.keyboard?.on('keydown-X', () => { if (this.active) scene.cameras.main.setZoom(2.8); });
    // + / - to scale selected sprite
    scene.input.keyboard?.on('keydown-PLUS', () => { if (this.active) this.scaleSelected(1.15); });
    scene.input.keyboard?.on('keydown-NUMPAD_ADD', () => { if (this.active) this.scaleSelected(1.15); });
    scene.input.keyboard?.on('keydown-MINUS', () => { if (this.active) this.scaleSelected(0.85); });
    scene.input.keyboard?.on('keydown-NUMPAD_SUBTRACT', () => { if (this.active) this.scaleSelected(0.85); });
    // Mouse wheel to scale when editor is active
    scene.input.on('wheel', (_pointer: any, _gameObjects: any, _deltaX: number, deltaY: number) => {
      if (!this.active || !this.lastSelected) return;
      const factor = deltaY < 0 ? 1.1 : 0.9;
      this.scaleSelected(factor);
    });
  }

  private lastSelected: Phaser.GameObjects.Image | null = null;

  private scaleSelected(factor: number): void {
    if (!this.lastSelected) return;
    const newScale = this.lastSelected.scale * factor;
    this.lastSelected.setScale(newScale);
    const item = this.editableSprites.find(e => e.sprite === this.lastSelected);
    if (item) {
      console.log(`[PlacementEditor] ${item.id} scale → ${newScale.toFixed(4)}`);
    }
  }

  /** Register a sprite as editable */
  register(sprite: Phaser.GameObjects.Image, id: string, type: 'npc' | 'micro'): void {
    this.editableSprites.push({ sprite, id, type });

    // Apply saved position from localStorage (if exists)
    const saved = localStorage.getItem('edu-world-positions');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        const list = type === 'npc' ? data.npcs : data.micros;
        const pos = list?.find((p: any) => p.id === id);
        if (pos) {
          sprite.x = pos.x;
          sprite.y = pos.y;
          if (pos.scale) sprite.setScale(pos.scale);
        }
      } catch { /* ignore parse errors */ }
    }
  }

  private toggle(): void {
    this.active = !this.active;

    if (this.active) {
      this.activate();
    } else {
      this.deactivate();
    }
  }

  private activate(): void {
    console.log('[PlacementEditor] ACTIVE — drag sprites, press S to export, Z/X for zoom');

    // Lock player movement
    if (this.playerController?.setInputLocked) {
      this.playerController.setInputLocked(true);
    }

    // Stop ALL tweens on editable sprites (idle animations block vertical dragging)
    for (const item of this.editableSprites) {
      this.scene.tweens.killTweensOf(item.sprite);
    }

    // Also make the player sprite draggable
    if (this.playerController?.getSprite) {
      const playerSprite = this.playerController.getSprite();
      if (playerSprite) {
        this.scene.tweens.killTweensOf(playerSprite);
        playerSprite.setInteractive({ draggable: true, useHandCursor: true });
        this.scene.input.setDraggable(playerSprite, true);
      }
    }

    // Show status text
    this.statusText = this.scene.add.text(10, 10, '📐 EDITOR MODE (N=exit, S=save, Z=zoom out, X=zoom in)\nDrag any sprite. Player is also draggable.', {
      fontSize: '10px',
      color: '#FFD166',
      backgroundColor: '#000000cc',
      padding: { x: 6, y: 4 },
    });
    this.statusText.setScrollFactor(0);
    this.statusText.setDepth(100);

    // Add labels to all sprites
    for (const item of this.editableSprites) {
      const label = this.scene.add.text(item.sprite.x, item.sprite.y - 12, item.id, {
        fontSize: '5px',
        color: item.type === 'npc' ? '#4fc3f7' : '#ffd54f',
        backgroundColor: '#000000aa',
        padding: { x: 2, y: 1 },
      });
      label.setOrigin(0.5);
      label.setDepth(99);
      this.labels.push(label);

      // Make interactive
      item.sprite.setInteractive({ draggable: true, useHandCursor: true });
    }

    // Drag events
    this.scene.input.on('dragstart', (_pointer: any, gameObject: any) => {
      this.dragging = gameObject;
      this.lastSelected = gameObject;
      gameObject.setAlpha(0.6);
    });

    this.scene.input.on('drag', (_pointer: any, gameObject: any, dragX: number, dragY: number) => {
      gameObject.x = dragX;
      gameObject.y = dragY;
      // Update editor label
      const idx = this.editableSprites.findIndex(e => e.sprite === gameObject);
      if (idx >= 0 && this.labels[idx]) {
        this.labels[idx].x = dragX;
        this.labels[idx].y = dragY - 12;
      }
      // Move associated NPC indicators (label, mission !, proximity sparkle)
      const item = this.editableSprites.find(e => e.sprite === gameObject);
      if (item && item.type === 'npc') {
        for (const child of this.scene.children.list) {
          if (child === gameObject) continue;
          if (child.getData && child.getData('npcOwner') === item.id) {
            const offsetY = child.getData('offsetY') || -22;
            (child as any).x = dragX;
            (child as any).y = dragY + offsetY;
          }
        }
      }
    });

    this.scene.input.on('dragend', (_pointer: any, gameObject: any) => {
      gameObject.setAlpha(1);
      this.dragging = null;
      // Log new position
      const item = this.editableSprites.find(e => e.sprite === gameObject);
      if (item) {
        console.log(`[PlacementEditor] ${item.id} → x: ${Math.round(gameObject.x)}, y: ${Math.round(gameObject.y)}`);
      }
    });
  }

  private deactivate(): void {
    console.log('[PlacementEditor] DEACTIVATED');

    // Unlock player movement
    if (this.playerController?.setInputLocked) {
      this.playerController.setInputLocked(false);
    }

    // Remove player draggable
    if (this.playerController?.getSprite) {
      const playerSprite = this.playerController.getSprite();
      if (playerSprite) {
        playerSprite.removeInteractive();
      }
    }

    // Remove labels
    for (const label of this.labels) label.destroy();
    this.labels = [];

    // Remove status text
    this.statusText?.destroy();
    this.statusText = null;

    // Remove interactivity
    for (const item of this.editableSprites) {
      item.sprite.removeInteractive();
      item.sprite.setAlpha(1);
    }

    // Remove drag listeners
    this.scene.input.off('dragstart');
    this.scene.input.off('drag');
    this.scene.input.off('dragend');

    // Restore zoom
    this.scene.cameras.main.setZoom(2.8);
  }

  private exportPositions(): void {
    const npcs: any[] = [];
    const micros: any[] = [];

    for (const item of this.editableSprites) {
      const pos = { id: item.id, x: Math.round(item.sprite.x), y: Math.round(item.sprite.y), scale: Number(item.sprite.scale.toFixed(4)) };
      if (item.type === 'npc') npcs.push(pos);
      else micros.push(pos);
    }

    const output = JSON.stringify({ npcs, micros }, null, 2);

    // Save to localStorage so positions persist across refreshes
    localStorage.setItem('edu-world-positions', output);

    // Log to console
    console.log('\n╔══════════════════════════════════════╗');
    console.log('║  POSITIONS SAVED TO LOCALSTORAGE     ║');
    console.log('╚══════════════════════════════════════╝');
    console.log(output);

    // Create downloadable file
    const blob = new Blob([output], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'edu-world-positions.json';
    a.click();
    URL.revokeObjectURL(url);

    // Update status
    if (this.statusText) {
      this.statusText.setText('📐 SAVED! Positions stored in localStorage + downloaded as JSON file.');
    }
  }
}
